/**
 * Section ids + navigation model for the Scheduler input page, shared by the
 * page (routing + title lookup) and the redesigned sidebar. The sidebar groups
 * these into numbered stages (Inputs / Schedule / Utilisation) per the handoff
 * `Sidebar.jsx`. Each data-table item carries its API slug so the sidebar can
 * show a loaded/edited freshness dot from `useDataStatus().tables[api]`.
 */

export const SECTIONS = {
  FACTORY_LAYOUT: 'factory-layout',
  PRODUCTION_REQUIREMENT: 'tab-0',
  WORKSTATION_DOWNTIME: 'tab-1',
  CLEANING_MAPPING: 'tab-2',
  RESOURCE_AND_MATERIAL_CONSTRAINTS: 'tab-3',
  RESOURCE_AND_INPUTS: 'tab-4',
  PACKAGING_ROUTES: 'tab-5',
  RESOURCE_AND_OUTPUT_MAPPING: 'tab-6',
  PRODUCT_MASTER: 'tab-7',
  PRODUCT_CLEAN_CHANGEOVER: 'tab-8',
  BASE_MATERIAL_CLEAN_CHANGEOVER: 'tab-9',
  SYNCHRONIZED_LINES: 'tab-10',
  RESOURCE_SWITCHING: 'tab-13',
  RESOURCE_CAPACITY_PROFILE: 'tab-14',
  TANKS: 'tab-15',
  TANK_WEEK_STATE: 'tab-16',
  KITCHEN_CYCLES: 'tab-17',
  STERILISER_TYPES: 'tab-11',
  STERILISER_ROUTING: 'tab-12',
  DATA_SNAPSHOTS: 'data-snapshots',
  COMMENTS: 'comments',
  RULES_CHANGEOVERS: 'rules-changeovers',
  RULES_CREW_SYNC: 'rules-crew-sync',
  RULES_STERILISER: 'rules-steriliser',
  RULES_RESOURCES: 'rules-resources',
  UTIL_ASSET: 'util-asset',
  UTIL_DAILY: 'util-daily',
  UTIL_CONSTRAINT: 'util-constraint',
  UTIL_CREW: 'util-crew',
  UTIL_DETAILS: 'util-details',
  UTIL_FG: 'util-fg',
  UTIL_INPUT: 'util-input',
  UTIL_KITCHEN: 'util-kitchen',
  UTIL_LINE: 'util-line',
  UTIL_CLEANING_SUMMARY: 'util-cleaning-summary',
} as const

export type SectionId = (typeof SECTIONS)[keyof typeof SECTIONS]

export type NavItem = { id: string; label: string; api?: string }

// Inputs-stage data tables. `api` is the data-status / SOR slug used for the
// freshness dot and matches the per-schedule endpoints.
export const RAW_DATA_ITEMS: NavItem[] = [
  {
    id: SECTIONS.PRODUCTION_REQUIREMENT,
    label: 'Production Requirement',
    api: 'production-requirements',
  },
  {
    id: SECTIONS.WORKSTATION_DOWNTIME,
    label: 'Workstation Downtime',
    api: 'workstation-downtime',
  },
  {
    id: SECTIONS.PRODUCT_MASTER,
    label: 'Product Master Data',
    api: 'product-master-data',
  },
  {
    id: SECTIONS.PACKAGING_ROUTES,
    label: 'Packaging Routes',
    api: 'packing-routes',
  },
  {
    id: SECTIONS.CLEANING_MAPPING,
    label: 'Cleaning Mapping',
    api: 'cleaning-mapping',
  },
  {
    id: SECTIONS.RESOURCE_AND_MATERIAL_CONSTRAINTS,
    label: 'Resource & Material Constraints',
    api: 'resource-and-material-constraints',
  },
  // Sits directly under its sibling because it is the same resource list
  // seen a different way: that tab owns each resource's attributes and its
  // single flat amount, this one owns the hour-by-hour override of that
  // amount. Its own entry rather than a second grid on that tab because it
  // is per-week (it carries a week selector) and that tab is not.
  {
    id: SECTIONS.RESOURCE_CAPACITY_PROFILE,
    label: 'Resource Capacity Profile',
    api: 'resource-capacity-profile',
  },
  {
    id: SECTIONS.RESOURCE_AND_INPUTS,
    label: 'Resource & Input Mapping',
    api: 'resource-and-input-mapping',
  },
  {
    id: SECTIONS.RESOURCE_AND_OUTPUT_MAPPING,
    label: 'Resource & Output Mapping',
    api: 'resource-and-output-mapping',
  },
  {
    id: SECTIONS.PRODUCT_CLEAN_CHANGEOVER,
    label: 'Product Clean Changeover',
    api: 'product-clean-changeover',
  },
  {
    id: SECTIONS.BASE_MATERIAL_CLEAN_CHANGEOVER,
    label: 'Base Material Changeover',
    api: 'base-material-clean-changeover',
  },
  {
    id: SECTIONS.SYNCHRONIZED_LINES,
    label: 'Synchronized Lines',
    api: 'synchronized-lines',
  },
  {
    id: SECTIONS.RESOURCE_SWITCHING,
    label: 'Resource Switching',
    api: 'resource-switching',
  },
  // Holding tanks. The vessel and its product eligibility describe the plant
  // and are week-invariant, so they share one tab; the opening stock and
  // closing target are what the planner retypes each week, so they get their
  // own — the same split, for the same reason, as Resource Capacity Profile
  // against its sibling above.
  {
    id: SECTIONS.TANKS,
    label: 'Holding Tanks',
    api: 'tanks',
  },
  {
    id: SECTIONS.TANK_WEEK_STATE,
    label: 'Tank Week State',
    api: 'tank-week-state',
  },
  // The order a kitchen runs a set of base materials in, week after week.
  {
    id: SECTIONS.KITCHEN_CYCLES,
    label: 'Kitchen Cycles',
    api: 'kitchen-cycles',
  },
  {
    id: SECTIONS.STERILISER_TYPES,
    label: 'Steriliser Types',
    api: 'steriliser-types',
  },
  {
    id: SECTIONS.STERILISER_ROUTING,
    label: 'Steriliser Routing',
    api: 'steriliser-routing',
  },
]

// Rules & Constraints sub-group (icons resolved in the sidebar component).
export type RuleNavItem = NavItem & {
  iconKey: 'swap_horiz' | 'groups' | 'thermostat' | 'rule'
}
export const RULES_ITEMS: RuleNavItem[] = [
  {
    id: SECTIONS.RULES_CHANGEOVERS,
    label: 'Changeovers',
    iconKey: 'swap_horiz',
  },
  { id: SECTIONS.RULES_CREW_SYNC, label: 'Rules', iconKey: 'rule' },
]

export const SCHEDULE_ITEMS: NavItem[] = [
  { id: SECTIONS.FACTORY_LAYOUT, label: 'Schedule' },
]

export const UTILISATION_ITEMS: NavItem[] = [
  { id: SECTIONS.UTIL_ASSET, label: 'Asset Utilisation' },
  { id: SECTIONS.UTIL_DAILY, label: 'Daily Utilisation' },
  { id: SECTIONS.UTIL_CONSTRAINT, label: 'Constraint Utilisation' },
  { id: SECTIONS.UTIL_CREW, label: 'Crew Utilisation' },
  { id: SECTIONS.UTIL_DETAILS, label: 'Details' },
  { id: SECTIONS.UTIL_FG, label: 'FG' },
  { id: SECTIONS.UTIL_INPUT, label: 'Input' },
  { id: SECTIONS.UTIL_KITCHEN, label: 'Kitchen' },
  { id: SECTIONS.UTIL_LINE, label: 'Line' },
  { id: SECTIONS.UTIL_CLEANING_SUMMARY, label: 'Cleaning Summary' },
]

export const DATA_SNAPSHOTS_ITEM: NavItem = {
  id: SECTIONS.DATA_SNAPSHOTS,
  label: 'Data & Snapshots',
}

// Raw Data tables whose values are authored as rules in the workbench → become
// a read-only source feed with a cross-link banner.
export const RULE_MANAGED: Record<string, { tab: string; label: string }> = {
  [SECTIONS.SYNCHRONIZED_LINES]: {
    tab: SECTIONS.RULES_CREW_SYNC,
    label: 'Crew & Sync',
  },
  [SECTIONS.STERILISER_TYPES]: {
    tab: SECTIONS.RULES_STERILISER,
    label: 'Steriliser',
  },
  [SECTIONS.STERILISER_ROUTING]: {
    tab: SECTIONS.RULES_STERILISER,
    label: 'Steriliser',
  },
  [SECTIONS.PRODUCT_CLEAN_CHANGEOVER]: {
    tab: SECTIONS.RULES_CHANGEOVERS,
    label: 'Changeovers',
  },
  [SECTIONS.RESOURCE_AND_MATERIAL_CONSTRAINTS]: {
    tab: SECTIONS.RULES_RESOURCES,
    label: 'Resources',
  },
}

/** All selectable items, for resolving the current section's display label. */
export const ALL_SECTION_ITEMS: NavItem[] = [
  { id: SECTIONS.FACTORY_LAYOUT, label: 'Schedule' },
  DATA_SNAPSHOTS_ITEM,
  ...RAW_DATA_ITEMS,
  ...RULES_ITEMS,
  // Keep retired sections resolvable for any deep-links still in the wild
  { id: SECTIONS.RULES_STERILISER, label: 'Steriliser' },
  { id: SECTIONS.RULES_RESOURCES, label: 'Resources' },
  ...UTILISATION_ITEMS,
]

/**
 * Sections hidden from the sidebar for now.
 *
 * Temporary and reversible: delete an entry to bring its tab back. The
 * sections themselves are untouched — their components, routes, data hooks and
 * labels all still exist, and hiding a tab has **no effect on the data or the
 * engine**. Synchronised Lines rows, steriliser types and steriliser routes
 * are still read from Delta and still constrain a run; this only removes the
 * places a planner can edit them from the UI.
 *
 * They stay listed in `ALL_SECTION_ITEMS` so `sectionLabel` keeps resolving —
 * the same reason the retired sections above are still there.
 */
export const HIDDEN_SECTIONS: ReadonlySet<string> = new Set<string>([
  SECTIONS.SYNCHRONIZED_LINES,
  SECTIONS.STERILISER_TYPES,
  SECTIONS.STERILISER_ROUTING,
  // The whole Rules & Constraints group. Its sub-heading disappears with the
  // last of its items, and `RULE_MANAGED`'s cross-links to it are suppressed
  // while it is hidden — a banner offering to open a tab that no longer
  // exists would navigate nowhere.
  SECTIONS.RULES_CHANGEOVERS,
  SECTIONS.RULES_CREW_SYNC,
])

/** True when this section should not be offered in the UI. */
export const isSectionHidden = (id: string): boolean => HIDDEN_SECTIONS.has(id)

export const sectionLabel = (id: string): string =>
  ALL_SECTION_ITEMS.find((i) => i.id === id)?.label ?? ''
